// "Mi certificado" de firma electrónica (autoservicio, 2026-09-14) —
// personal-medico y director-tecnico-farmacia vinculan/revocan su propio
// certificado .p12 personal (Modelo A, Modelo B cancelado — ver PLAN.md).
//
// Sin <TopNav>/<FarmaciaApp> chrome a propósito: esta pantalla la ven DOS
// roles con navs distintos (TopNav para médico, subnav propio para el
// Director Técnico) — un link simple desde cada uno trae acá, en vez de
// forzar esta pantalla dentro de cualquiera de los dos arrays `TABS`
// tipados existentes. El backend (`medicoConCertificadoAutorizado`) es
// quien de verdad decide quién puede usarla; acá solo se muestra el error
// si el backend devuelve 403.
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { listarCertificados, revocarCertificado, subirCertificado, type CertificadoFirma } from '@/lib/certificadoClient';

function badgeDeEstado(cert: CertificadoFirma): { variant: 'pine' | 'clay' | 'secondary'; texto: string } {
  if (cert.estado !== 'activo') return { variant: 'secondary', texto: cert.estado };
  if (cert.diasParaVencer <= 0) return { variant: 'clay', texto: 'vencido' };
  if (cert.diasParaVencer <= 30) return { variant: 'clay', texto: `vence en ${cert.diasParaVencer} días` };
  return { variant: 'pine', texto: 'activo' };
}

export function MiCertificadoPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [certificados, setCertificados] = useState<CertificadoFirma[] | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function recargar(): Promise<void> {
    try {
      setCertificados(await listarCertificados());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Sin esto, `certificados` se queda en `null` para siempre y la
      // pantalla muestra el error Y "Cargando…" a la vez (bug real visto
      // en vivo 2026-09-14: sesión de Medplum expirada → 401 → error fijo
      // con el spinner de carga nunca resuelto).
      setCertificados([]);
    }
  }

  useEffect(() => {
    void recargar();
  }, []);

  const activo = (certificados ?? []).find((c) => c.estado === 'activo') ?? null;

  async function confirmarSubida(): Promise<void> {
    if (!archivo || !password) return;
    setCargando(true);
    setError(null);
    try {
      const p12 = new Uint8Array(await archivo.arrayBuffer());
      await subirCertificado({ p12, password });
      setArchivo(null);
      setPassword('');
      await recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setCargando(false);
  }

  async function confirmarRevocar(id: number): Promise<void> {
    setCargando(true);
    setError(null);
    try {
      await revocarCertificado(id);
      await recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setCargando(false);
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-hce-ink">{t('miCertificado.titulo')}</h1>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          {t('miCertificado.volver')}
        </Button>
      </div>

      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {certificados === null ? (
        <p className="text-sm text-hce-muted">{t('miCertificado.cargando')}</p>
      ) : activo ? (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-hce-ink">{activo.titularNombre}</span>
            <Badge variant={badgeDeEstado(activo).variant}>{badgeDeEstado(activo).texto}</Badge>
          </div>
          <p className="text-xs text-hce-muted">
            {t('miCertificado.emisor')}: {activo.emisor}
          </p>
          <p className="text-xs text-hce-muted">
            {t('miCertificado.vigenciaHasta')}: {new Date(activo.vigenteHasta).toLocaleDateString('es-EC')}
          </p>
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" disabled={cargando} onClick={() => void confirmarRevocar(activo.id)}>
              {t('miCertificado.revocar')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-hce-muted">{t('miCertificado.sinCertificado')}</p>
      )}

      <div className="flex flex-col gap-2 rounded-md border border-border p-3">
        <p className="text-sm font-medium text-hce-ink">
          {activo ? t('miCertificado.reemplazarTitulo') : t('miCertificado.vincularTitulo')}
        </p>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".p12,.pfx"
            aria-label={t('miCertificado.elegirArchivo')}
            className="hidden"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            {t('miCertificado.elegirArchivo')}
          </Button>
          {archivo && <span className="truncate text-sm text-hce-ink">{archivo.name}</span>}
        </div>
        <div>
          <Label htmlFor="certificado-password">{t('miCertificado.passwordLabel')}</Label>
          <Input
            id="certificado-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!archivo || !password || cargando} onClick={() => void confirmarSubida()}>
            {cargando ? t('miCertificado.subiendo') : t('miCertificado.subir')}
          </Button>
        </div>
      </div>
    </div>
  );
}
